import moment from "moment";

class TourValidationService {
  /**
   * Valida el formulario completo de tour
   * @param {Object} formData - Datos del formulario
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Información de precios
   * @param {Array} models - Lista de modelos disponibles
   * @param {Array} vehicles - Lista de vehículos disponibles
   * @param {Array} gpsDevices - Lista de dispositivos GPS disponibles
   * @returns {Object} Resultado de validación con errores y estado
   */
  static validateForm(formData, tourData, pricing, models, vehicles, gpsDevices) {
    const errors = {};
    
    if (!formData || !tourData) {
      return {
        isValid: false,
        errors: { general: "Datos del formulario incompletos" }
      };
    }

    // Validaciones básicas que siempre se pueden hacer
    if (!formData.vehicle || !formData.vehicle.modelId) {
      errors.vehicle = "Debe seleccionar un modelo de vehículo";
    }

    if (!formData.options) {
      errors.options = "Debe configurar las opciones del tour";
    }

    // Validaciones de vehículo (solo si tenemos models y vehicles con datos)
    if (models && Array.isArray(models) && models.length > 0 && 
        vehicles && Array.isArray(vehicles) && vehicles.length > 0) {
      const vehicleErrors = this.validateVehicleSelection(formData, models, vehicles);
      Object.assign(errors, vehicleErrors);
    }

    // Validaciones de GPS (solo si tenemos gpsDevices con datos)
    if (gpsDevices && Array.isArray(gpsDevices) && gpsDevices.length > 0) {
      const gpsErrors = this.validateGpsSelection(formData, gpsDevices);
      Object.assign(errors, gpsErrors);
    }

    // Validaciones de precios (solo si tenemos pricing)
    if (pricing) {
      const pricingErrors = this.validatePricing(formData, pricing);
      Object.assign(errors, pricingErrors);
    }

    // Validaciones de descuentos (solo si tenemos pricing)
    if (pricing) {
      const discountErrors = this.validateDiscounts(formData, pricing);
      Object.assign(errors, discountErrors);
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  }

  /**
   * Valida la selección de vehículo
   * @param {Object} formData - Datos del formulario
   * @param {Array} models - Lista de modelos
   * @param {Array} vehicles - Lista de vehículos
   * @returns {Object} Errores de validación de vehículo
   */
  static validateVehicleSelection(formData, models, vehicles) {
    const errors = {};

    // Validar que se haya seleccionado un modelo
    if (!formData.vehicle.modelId) {
      errors.model = "Debe seleccionar un modelo de vehículo";
    }

    // Validar que se haya seleccionado un vehículo (excepto para moto propia)
    if (models && Array.isArray(models)) {
      const selectedModel = models.find(m => m.id === formData.vehicle.modelId);
      if (selectedModel && selectedModel.model_name !== "Own Motorcycle") {
        if (!formData.vehicle.vehicleId) {
          errors.vehicle = "Debe seleccionar un vehículo";
        } else {
          // Validar que el vehículo esté disponible
          if (vehicles && Array.isArray(vehicles)) {
            const selectedVehicle = vehicles.find(v => v.id === formData.vehicle.vehicleId);
            if (!selectedVehicle) {
              errors.vehicle = "El vehículo seleccionado no está disponible";
            }
          }
        }
      }
    }

    return errors;
  }

  /**
   * Valida la selección de GPS
   * @param {Object} formData - Datos del formulario
   * @param {Array} gpsDevices - Lista de dispositivos GPS
   * @returns {Object} Errores de validación de GPS
   */
  static validateGpsSelection(formData, gpsDevices) {
    const errors = {};

    if (formData.options.gps) {
      if (!formData.vehicle.gpsDeviceId) {
        errors.gps = "Debe seleccionar un dispositivo GPS";
      } else {
        // Validar que el dispositivo GPS esté disponible
        if (gpsDevices && Array.isArray(gpsDevices)) {
          const selectedGps = gpsDevices.find(g => g.id === formData.vehicle.gpsDeviceId);
          if (!selectedGps) {
            errors.gps = "El dispositivo GPS seleccionado no está disponible";
          }
        }
      }
    }

    return errors;
  }

  /**
   * Valida los precios y montos
   * @param {Object} formData - Datos del formulario
   * @param {Object} pricing - Información de precios
   * @returns {Object} Errores de validación de precios
   */
  static validatePricing(formData, pricing) {
    const errors = {};

    // Validar que el valor a cargar no sea negativo
    if (formData.payment.amount < 0) {
      errors.amount = "El valor a cargar no puede ser negativo";
    }

    // Validar que el valor a cargar no exceda el total
    if (formData.payment.amount > pricing.finalTotal) {
      errors.amount = "El valor a cargar no puede exceder el total del tour";
    }

    // Validar que el pago parcial sea válido
    if (formData.payment.partial > 0) {
      if (formData.payment.partial > formData.payment.amount) {
        errors.partial = "El pago parcial no puede exceder el valor a cargar";
      }
    }

    return errors;
  }

  /**
   * Valida los descuentos
   * @param {Object} formData - Datos del formulario
   * @param {Object} pricing - Información de precios
   * @returns {Object} Errores de validación de descuentos
   */
  static validateDiscounts(formData, pricing) {
    const errors = {};

    // Validar descuento
    if (formData.discounts.discount > pricing.finalTotal) {
      errors.discount = "El descuento no puede exceder el total del tour";
    }

    // Validar recompensas
    if (formData.discounts.rewards > formData.clientRewards) {
      errors.rewards = "Las recompensas solicitadas no están disponibles";
    }

    // Validar que la suma de descuentos no exceda el total
    const totalDiscounts = (formData.discounts.discount || 0) + (formData.discounts.rewards || 0);
    if (totalDiscounts > pricing.finalTotal) {
      errors.discounts = "La suma de descuentos no puede exceder el total del tour";
    }

    return errors;
  }

  /**
   * Valida las fechas
   * @param {Object} formData - Datos del formulario
   * @param {Object} tourData - Datos del tour
   * @returns {Object} Errores de validación de fechas
   */
  static validateDates(formData, tourData) {
    const errors = {};

    // Validar que las fechas del tour sean válidas
    if (tourData.booking.tour.tour_start_date && tourData.booking.tour.tour_end_date) {
      const startDate = moment(tourData.booking.tour.tour_start_date);
      const endDate = moment(tourData.booking.tour.tour_end_date);

      if (endDate.isBefore(startDate)) {
        errors.dates = "La fecha de fin no puede ser anterior a la fecha de inicio";
      }

      // Validar que el tour no haya pasado
      const currentDate = moment();
      if (startDate.isBefore(currentDate)) {
        errors.dates = "No se puede procesar un tour que ya ha pasado";
      }
    }

    return errors;
  }

  /**
   * Valida las opciones seleccionadas
   * @param {Object} formData - Datos del formulario
   * @param {Object} tourData - Datos del tour
   * @returns {Object} Errores de validación de opciones
   */
  static validateOptions(formData, tourData) {
    const errors = {};

    // Validar que no se seleccione habitación individual si hay pasajero
    if (formData.options.single && tourData.booking.p_first_name) {
      errors.single = "No se puede seleccionar habitación individual cuando hay pasajero";
    }

    // Validar que no se seleccione habitación doble si no hay pasajero
    if (!formData.options.single && !tourData.booking.p_first_name) {
      errors.double = "Debe seleccionar habitación individual cuando no hay pasajero";
    }

    return errors;
  }

  /**
   * Valida si un vehículo está disponible para las fechas del tour
   * @param {Object} vehicle - Vehículo a validar
   * @param {string} tourStartDate - Fecha de inicio del tour
   * @param {string} tourEndDate - Fecha de fin del tour
   * @returns {boolean} Si está disponible
   */
  static isVehicleAvailableForDates(vehicle, tourStartDate, tourEndDate) {
    if (!vehicle.date_2) {
      return true; // Vehículo sin restricciones de fecha
    }

    const vehicleEndDate = moment(vehicle.date_2);
    const tourStart = moment(tourStartDate);
    const tourEnd = moment(tourEndDate);

    // Verificar si hay conflicto de fechas
    return !(tourStart.isBefore(vehicleEndDate) && tourEnd.isAfter(vehicleEndDate));
  }

  /**
   * Valida si un dispositivo GPS está disponible
   * @param {Object} gpsDevice - Dispositivo GPS a validar
   * @returns {boolean} Si está disponible
   */
  static isGpsDeviceAvailable(gpsDevice) {
    return gpsDevice && gpsDevice.availability === true;
  }

  /**
   * Valida las reglas de negocio específicas
   * @param {Object} formData - Datos del formulario
   * @param {Object} tourData - Datos del tour
   * @returns {Object} Errores de reglas de negocio
   */
  static validateBusinessRules(formData, tourData) {
    const errors = {};

    // Validar que el tour no sea P4M si se intenta usar Stripe
    if (tourData.booking.p4m && formData.payment.type === "stripe") {
      errors.payment = "No se puede usar Stripe para tours P4M";
    }

    // Validar que el cliente tenga datos de Stripe si se usa Stripe
    if (formData.payment.type === "stripe" && !tourData.driver.stripe_tours) {
      errors.payment = "El cliente no tiene datos de Stripe configurados";
    }

    // Validar límites de descuento según el tipo de tour
    if (tourData.booking.tour.tour_category_id === 3) {
      if (formData.discounts.rewards > 0) {
        errors.rewards = "No se pueden usar recompensas en este tipo de tour";
      }
    }

    return errors;
  }

  /**
   * Valida los permisos del usuario
   * @param {Array} userPermissions - Permisos del usuario
   * @returns {Object} Errores de permisos
   */
  static validatePermissions(userPermissions) {
    const errors = {};

    const requiredPermissions = [1, 2, 4, 6, 9];
    const hasPermission = userPermissions.some(permission => 
      requiredPermissions.includes(permission)
    );

    if (!hasPermission) {
      errors.permissions = "No tiene permisos para procesar tours";
    }

    return errors;
  }

  /**
   * Valida la disponibilidad de recursos
   * @param {Array} vehicles - Lista de vehículos
   * @param {Array} gpsDevices - Lista de dispositivos GPS
   * @param {Object} formData - Datos del formulario
   * @returns {Object} Errores de disponibilidad
   */
  static validateResourceAvailability(vehicles, gpsDevices, formData) {
    const errors = {};

    // Validar disponibilidad de vehículos
    if (formData.vehicle.vehicleId) {
      const selectedVehicle = vehicles.find(v => v.id === formData.vehicle.vehicleId);
      if (selectedVehicle && !selectedVehicle.availability) {
        errors.vehicle = "El vehículo seleccionado no está disponible";
      }
    }

    // Validar disponibilidad de GPS
    if (formData.vehicle.gpsDeviceId) {
      const selectedGps = gpsDevices.find(g => g.id === formData.vehicle.gpsDeviceId);
      if (selectedGps && !selectedGps.availability) {
        errors.gps = "El dispositivo GPS seleccionado no está disponible";
      }
    }

    return errors;
  }

  /**
   * Genera advertencias (no errores críticos)
   * @param {Object} formData - Datos del formulario
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Información de precios
   * @returns {Array} Lista de advertencias
   */
  static generateWarnings(formData, tourData, pricing) {
    const warnings = [];

    // Advertencia si el tour está próximo a comenzar
    const tourStartDate = moment(tourData.booking.tour.tour_start_date);
    const currentDate = moment();
    const daysUntilTour = tourStartDate.diff(currentDate, "days");

    if (daysUntilTour < 7) {
      warnings.push("El tour comienza en menos de una semana");
    }

    // Advertencia si el valor a cargar es muy bajo
    if (formData.payment.amount < pricing.finalTotal * 0.1) {
      warnings.push("El valor a cargar es muy bajo comparado con el total");
    }

    // Advertencia si no se seleccionó seguro de cancelación
    if (!formData.options.cancelInsurance && daysUntilTour > 60) {
      warnings.push("Considere agregar seguro de cancelación para tours con más de 60 días");
    }

    return warnings;
  }

  /**
   * Valida el formulario en tiempo real (para validación reactiva)
   * @param {Object} formData - Datos del formulario
   * @param {string} fieldName - Nombre del campo a validar
   * @returns {string|null} Mensaje de error o null si es válido
   */
  static validateField(formData, fieldName) {
    switch (fieldName) {
      case "vehicle.modelId":
        return formData.vehicle.modelId ? null : "Debe seleccionar un modelo";
      
      case "vehicle.vehicleId":
        if (!formData.vehicle.modelId) return null;
        const selectedModel = (formData.models && formData.models.find(m => m.id === formData.vehicle.modelId));
        if (selectedModel && selectedModel.model_name === "Own Motorcycle") return null;
        return formData.vehicle.vehicleId ? null : "Debe seleccionar un vehículo";
      
      case "vehicle.gpsDeviceId":
        return formData.options.gps && !formData.vehicle.gpsDeviceId 
          ? "Debe seleccionar un dispositivo GPS" 
          : null;
      
      case "payment.amount":
        if (formData.payment.amount < 0) return "El valor no puede ser negativo";
        if (formData.pricing && formData.payment.amount > formData.pricing.finalTotal) {
          return "El valor no puede exceder el total";
        }
        return null;
      
      case "discounts.discount":
        if (formData.discounts.discount < 0) return "El descuento no puede ser negativo";
        if (formData.pricing && formData.discounts.discount > formData.pricing.finalTotal) {
          return "El descuento no puede exceder el total";
        }
        return null;
      
      case "discounts.rewards":
        if (formData.discounts.rewards < 0) return "Las recompensas no pueden ser negativas";
        if (formData.discounts.rewards > formData.clientRewards) {
          return "Las recompensas solicitadas no están disponibles";
        }
        return null;
      
      default:
        return null;
    }
  }
}

export default TourValidationService; 


// WEBPACK FOOTER //
// ./src/services/tourValidationService.js